import axios from 'axios';

import { forwardRef, useEffect, useState } from 'react';
import { getAnalytics, logEvent } from "firebase/analytics";
import { Modal } from '@mui/material';

// Relative time.
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Redux.
import { connect } from 'react-redux';

// Components.
import BottomMenu from './BottomMenu';
import GroupedNotes from './GroupedNotes';
import './NotesFeed.css';
import Loading from './Loading';
import SigninButtonPopup from './SigninButtonPopup';


import { validTokenExists } from '../firebaseHelper';

const SigninButtonPopupForwardRef = forwardRef((props, ref) => (
    <SigninButtonPopup {...props} />
));


const NotesContent = ({
}) => {
    const analytics = getAnalytics();

    const [refresh, setRefresh] = useState(false);

    const [groupedNotes, setGroupedNotes] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const [votes, setVotes] = useState({});

    const [openSigninButtonPopup, setOpenSigninButtonPopup] = useState(false);

    const toggleSigninButtonPopup = (open) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setOpenSigninButtonPopup(open);
    }

    useEffect(() => {
        dayjs.extend(relativeTime);

        logEvent(analytics, 'screen_view', {
            firebase_screen: 'notes'
        });
        setRefresh(true);
    }, []);

    useEffect(() => {
        if (refresh) {
            setGroupedNotes([]);
            setVotes({});
            setErrorMessage(null);
            fetchNotes();
        }
    }, [refresh]);

    const fetchNotesHelper = (endpoint) => {
        let jsonBody = {
        };
        axios
            .post(`/${endpoint}`, jsonBody)
            .then((res) => {
                if (res.data.error) {
                    // TODO display error message to user...
                    console.log(res.data.error);
                    setErrorMessage("Unexpected error occurred.");
                } else {
                    // Process fetched notes...
                    if (res.data.groupedNotes.length === 0) {
                        // no notes returned.
                    } else {
                        setGroupedNotes(prev => [...prev, ...res.data.groupedNotes]);
                        const newVotes = { ...votes };
                        for (const note of res.data.votes) {
                            newVotes[note.noteId] = note;
                        }
                        setVotes(newVotes);
                    }
                    setErrorMessage(null);
                }
                setRefresh(false);
            })
            .catch((err) => {
                // TODO display error message to user...
                console.log(err.response?.data?.error);
                setErrorMessage('Couldn’t load notes. Please try again.');
                setRefresh(false);
            });
    };

    const fetchNotes = () => {
        validTokenExists()
            .then(() => {
                fetchNotesHelper('notes_trending');
            })
            .catch(() => {
                // Not logged in.
                fetchNotesHelper('notes_trending_no_auth');
            });
    };

    const voteUp = (noteId) => () => {
        updateVote(noteId, true);
    };

    const voteDown = (noteId) => () => {
        updateVote(noteId, false);
    };

    const deleteNote = (noteId) => () => {
        validTokenExists()
            .then(() => {
                let jsonBody = {
                    noteId: noteId,
                };
                axios
                    .post('/note_delete', jsonBody)
                    .then((res) => {
                        if (res.data.error) {
                            // TODO display error message to user...
                            console.log(res.data.error);
                            setErrorMessage("Unexpected error occurred.");
                        } else {
                            // Process deleted note...
                            setGroupedNotes(prev => {
                                const newGroupedNotes = prev.map(group => {
                                    return {
                                        ...group,
                                        notes: group.notes.filter(note => note.id !== noteId),
                                    };
                                }).filter(group => group.notes.length > 0);
                                return newGroupedNotes;
                            });
                            setErrorMessage(null);
                        }
                    })
                    .catch((err) => {
                        // TODO display error message to user...
                        console.log(err.response?.data?.error);
                        setErrorMessage("Unexpected error occurred.");
                    });
            })
            .catch(() => {
                // Not logged in.
                setOpenSigninButtonPopup(true);
            });
    };

    const updateVote = (noteId, vote) => {
        validTokenExists()
            .then(() => {
                const oldVote = votes[noteId];
                let newStatus = vote;
                if (oldVote && oldVote.voteStatus === vote) {
                    newStatus = null;
                    const newVotes = { ...votes };
                    delete newVotes[noteId];
                    setVotes(newVotes);
                } else {
                    setVotes(prev => ({
                        ...prev,
                        [noteId]: {
                            noteId: noteId,
                            voteStatus: newStatus,
                        }
                    }));
                }

                const upVoteDelta = (newStatus === true ? 1 : 0) - (oldVote && oldVote.voteStatus === true ? 1 : 0);
                const downVoteDelta = (newStatus === false ? 1 : 0) - (oldVote && oldVote.voteStatus === false ? 1 : 0);
                // Update groupedNotes.notes counts optimistically.
                const newGroupedNotes = groupedNotes.map(group => {
                    const newNotes = group.notes.map(note => {
                        if (note.id === noteId) {
                            return {
                                ...note,
                                upVotes: note.upVotes + upVoteDelta,
                                downVotes: note.downVotes + downVoteDelta,
                            };
                        } else {
                            return note;
                        }
                    });
                    return {
                        ...group,
                        notes: newNotes,
                    };
                });
                setGroupedNotes(newGroupedNotes);

                let jsonBody = {
                    noteId: noteId,
                    voteStatus: newStatus,
                };
                axios
                    .post('/note_vote', jsonBody)
                    .then((res) => {
                        if (res.data.error) {
                            // TODO display error message to user...
                            console.log(res.data.error);
                            if (oldVote) {
                                setVotes(prev => ({
                                    ...prev,
                                    [noteId]: oldVote,
                                }));
                            }
                        }
                    })
                    .catch((err) => {
                        // TODO display error message to user...
                        console.log(err.response?.data?.error);
                        if (oldVote) {
                            setVotes(prev => ({
                                ...prev,
                                [noteId]: oldVote,
                            }));
                        }
                    });
            })
            .catch(() => {
                // Not logged in.
                setOpenSigninButtonPopup(true);
            });
    }

    return (
        <div className='container hideScrollbar appScreen notesFeed'>
            <Modal
                open={openSigninButtonPopup}
                onClose={toggleSigninButtonPopup(false)}
            >
                <SigninButtonPopupForwardRef close={toggleSigninButtonPopup(false)} />
            </Modal>
            <div>
                <div className='hasBottomMenu'>
                    <div style={{ padding: '20px 20px 0px', }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className='notesTrendingTitle' style={{ flexGrow: '1', verticalAlign: 'middle' }}>
                                Trending notes from other listeners
                            </div>
                        </div>
                    </div>
                    <div style={{ padding: '20px 0px' }}>
                        {errorMessage && <div className='notesFeedError' role='alert'><p>{errorMessage}</p><button type='button' onClick={() => setRefresh(true)}>Try again</button></div>}
                        {refresh ?
                            <Loading overrideStyle={{ padding: '30px', textAlign: 'center' }} />
                            :
                            groupedNotes.length === 0 && !errorMessage ?
                                <div className='notesEmptyState'>
                                    <h2>No notes yet</h2>
                                    <p>Notes from other listeners will appear here.</p>
                                </div>
                                :
                                <div>
                                    <div style={{ padding: '0px 20px' }}>
                                        {groupedNotes.map((group) => (
                                            <div key={group.id || group.notes[0]?.id} style={{ marginBottom: '20px' }}>
                                                <GroupedNotes
                                                    showsShowName
                                                    deleteNote={deleteNote}
                                                    group={group}
                                                    votes={votes}
                                                    voteUp={voteUp}
                                                    voteDown={voteDown}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                        }

                    </div>
                    <BottomMenu />
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
})

const mapActionsToProps = {
}

export default connect(mapStateToProps, mapActionsToProps)(NotesContent);
